import { useTranslation } from 'react-i18next';
import type {
  StandingsResponse,
  TiebreakCriterion,
} from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { viewerHighlight } from '@/lib/viewerRow';
import { formatTiebreak, tiebreakLabel } from '@/lib/tiebreak-labels';
import { PlayerNameLink } from '@/components/PlayerNameLink';

// Story 083 — extracted from StandingsPage so the public standings page
// and the hub's director-facing Standings sub-page render an identical
// table. Pure presentational: no fetching, no polling. All data-testids
// are preserved verbatim so the existing standings cucumber suite keeps
// passing regardless of which page mounts it.

interface Props {
  data: StandingsResponse;
  /** Highlights the signed-in viewer's own row when their profile id
   *  matches a standings row. */
  viewerUserId?: string | null;
  /** When true, flag the rank-1 row as the winner (used by the hub
   *  Standings sub-page once the tournament is completed). */
  highlightWinner?: boolean;
  /** Story 110 — when set, player names link to
   *  /t/<slug>/player/<startingRank>. Public pages pass the slug;
   *  director-hub and TV surfaces leave names as plain text. */
  playerLinkSlug?: string;
}

export function StandingsTable({ data, viewerUserId, highlightWinner, playerLinkSlug }: Props) {
  const { t } = useTranslation('tournament');
  const showRating = getGameConfig(data.game).playerFields.rating;

  return (
    <div className="overflow-x-auto rounded border">
      <table data-testid="standings-table" className="w-full text-left">
        <thead className="border-b bg-muted/50">
          <tr>
            <th className="w-6 px-1 py-2"></th>
            <th className="w-12 px-2 py-2 text-xs font-medium text-muted-foreground">{t('standings.table.rank')}</th>
            <th className="px-2 py-2 text-xs font-medium text-muted-foreground">{t('standings.table.name')}</th>
            {showRating && (
              <th className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">{t('standings.table.rating')}</th>
            )}
            <th className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">{t('standings.table.points')}</th>
            <th className="w-12 px-2 py-2 text-xs font-medium text-muted-foreground">{t('standings.table.byes')}</th>
            <th
              className="w-12 px-2 py-2 text-xs font-medium text-muted-foreground"
              title={t('standings.table.games_played_title')}
            >
              {t('standings.table.games_played')}
            </th>
            {data.tiebreakCriteria.map((criterion) => (
              <th
                key={criterion}
                data-testid={`tiebreak-column-${criterion}`}
                className="w-20 px-2 py-2 text-xs font-medium text-muted-foreground"
              >
                {tiebreakLabel(criterion, t)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.standings.map((row) => {
            const name = `${row.firstName} ${row.lastName}`.trim();
            const mine = viewerHighlight(row.profileUserId, viewerUserId ?? undefined);
            const isWinner =
              highlightWinner &&
              row.rank === 1 &&
              row.status !== 'eliminated' &&
              row.status !== 'withdrawn';
            return (
              <tr
                key={`${row.startingRank}`}
                data-testid={`standings-row-${name}`}
                data-viewer-row={mine.isMine ? 'true' : undefined}
                data-winner-row={isWinner ? 'true' : undefined}
                className={`border-b hover:bg-muted/30 ${mine.className}`}
              >
                <td className="w-6 px-1 py-2 text-center text-sm text-muted-foreground">
                  {(row.status === 'eliminated' || row.status === 'withdrawn') && (
                    <span
                      data-testid="player-out-marker"
                      title={
                        row.status === 'eliminated'
                          ? t('standings.out_marker.eliminated')
                          : t('standings.out_marker.withdrawn')
                      }
                    >
                      ✕
                    </span>
                  )}
                  {isWinner && (
                    <span data-testid="standings-winner-marker" title={t('standings.winner')}>
                      🏆
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-sm" data-testid={`standings-rank-${name}`}>
                  {row.rank}
                </td>
                <td className="px-2 py-2 text-sm font-medium">
                  {playerLinkSlug ? (
                    <PlayerNameLink
                      slug={playerLinkSlug}
                      startingRank={row.startingRank}
                      name={name}
                    />
                  ) : (
                    name
                  )}
                </td>
                {showRating && (
                  <td className="px-2 py-2 text-sm text-muted-foreground">
                    {row.rating ?? ''}
                  </td>
                )}
                <td className="px-2 py-2 text-sm font-semibold" data-testid={`standings-points-${name}`}>
                  {row.points}
                </td>
                <td className="px-2 py-2 text-sm text-muted-foreground">
                  {row.byes || ''}
                </td>
                <td className="px-2 py-2 text-sm text-muted-foreground">
                  {row.gamesPlayed}
                </td>
                {data.tiebreakCriteria.map((criterion) => (
                  <td
                    key={criterion}
                    className="px-2 py-2 text-sm"
                    data-testid={`standings-tiebreak-${criterion}-${name}`}
                  >
                    {formatTiebreak(criterion, row.tiebreaks[criterion] ?? 0)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
